import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CheckCircle, CloudDownload, VideoCall, WarningAmber } from '@mui/icons-material';
import { EmptyState, IconTile } from '../pagekit';
import { getExternalDomains, getMeetingDuration } from './transcriptUtils';

/**
 * One domain line in the domain dialogs.
 * @param {object} props
 * @param {'warning'|'success'|'neutral'} props.tone - Tone.
 * @param {React.ReactNode} props.icon - Icon.
 * @param {string} props.domain - Domain.
 * @param {React.ReactNode} props.detail - Secondary text.
 * @param {React.ReactNode} [props.trailing] - Right side content.
 * @param {React.ReactNode} [props.leading] - Replaces the icon (e.g. a checkbox).
 * @param {Function} [props.onClick] - Row click.
 * @returns {JSX.Element}
 */
function DomainRow({ tone, icon, domain, detail, trailing, leading, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.25,
        mb: 1,
        border: 1,
        borderColor: 'divider',
        borderRadius: 'var(--q-radius-lg)',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { bgcolor: 'action.hover' } : undefined,
      }}
    >
      {leading || <IconTile tone={tone} size={30}>{icon}</IconTile>}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>{domain}</Typography>
        <Typography variant="caption" color="text.secondary">{detail}</Typography>
      </Box>
      {trailing}
    </Box>
  );
}

/**
 * Pick Fathom meetings to import into the project.
 * @param {object} props
 * @param {ReturnType<import('./useFathomImport').default>} props.fathom - Hook state.
 * @returns {JSX.Element}
 */
export function FathomImportDialog({ fathom }) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    open, closeImport, meetings, loading, loadingMore, error, hasIntegration, selected, selectedCount,
    allSelected, toggleSelected, toggleSelectAll, importing, progress, weeksBack, loadOlder, importSelected,
  } = fathom;
  const weeksLabel = `${weeksBack} week${weeksBack !== 1 ? 's' : ''}`;

  return (
    <Dialog open={open} onClose={closeImport} maxWidth="md" fullWidth fullScreen={isPhone} disableRestoreFocus>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VideoCall fontSize="small" />
        Import meetings from Fathom
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity={error.includes('Imported') ? 'warning' : 'error'} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {hasIntegration === false && !loading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No Fathom API key found. Add your Fathom key in Settings, Integrations first.
          </Alert>
        )}

        {loading && (
          <Box role="progressbar" aria-label="Loading meetings from Fathom" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1, borderRadius: 'var(--q-radius-lg)' }} />
            ))}
          </Box>
        )}

        {!loading && meetings.length === 0 && hasIntegration !== false && !error && (
          <EmptyState
            compact
            icon={<VideoCall />}
            title="No meetings found"
            description={`Nothing was recorded in the past ${weeksLabel}.`}
            actions={<Button variant="outlined" onClick={loadOlder}>Search older meetings</Button>}
          />
        )}

        {!loading && meetings.length > 0 && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }} role="status">
                {meetings.length} meeting{meetings.length !== 1 ? 's' : ''} from the past {weeksLabel}
                {selectedCount > 0 && ` · ${selectedCount} selected`}
              </Typography>
              <Button size="small" onClick={loadOlder} disabled={loadingMore || importing}>
                Load older
              </Button>
              <Button size="small" onClick={toggleSelectAll} disabled={importing}>
                {allSelected ? 'Deselect all' : 'Select all'}
              </Button>
            </Box>
            <Divider sx={{ mb: 1 }} />

            <Box sx={{ maxHeight: { sm: 420 }, overflow: 'auto' }}>
              {meetings.map((meeting) => {
                const meetingTitle = meeting.title || meeting.meeting_title || 'Untitled Meeting';
                const meetingDate = meeting.scheduled_start_time || meeting.created_at;
                const durationMin = getMeetingDuration(meeting);
                const isSelected = Boolean(selected[meeting.recording_id]);
                const externalDomains = getExternalDomains(meeting);
                const attendeeCount = (meeting.calendar_invitees || []).length;

                return (
                  <Box
                    key={meeting.recording_id}
                    component="label"
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      p: 1.25,
                      mb: 1,
                      border: 1,
                      borderColor: isSelected ? 'var(--q-accent-border)' : 'divider',
                      borderRadius: 'var(--q-radius-lg)',
                      bgcolor: isSelected ? 'var(--q-accent-tint)' : 'background.paper',
                      cursor: importing ? 'default' : 'pointer',
                      '&:hover': { borderColor: importing ? undefined : 'var(--q-border-hover)' },
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => toggleSelected(meeting.recording_id)}
                      disabled={importing}
                      sx={{ mt: -0.5 }}
                      inputProps={{ 'aria-label': `Select ${meetingTitle}` }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>{meetingTitle}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.25, flexWrap: 'wrap' }}>
                        {meetingDate && (
                          <Typography variant="caption" color="text.secondary">
                            {new Date(meetingDate).toLocaleDateString(undefined, {
                              year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                            })}
                          </Typography>
                        )}
                        {durationMin != null && <Chip label={`${durationMin} min`} size="small" variant="outlined" />}
                        {attendeeCount > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {attendeeCount} attendee{attendeeCount !== 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Box>
                      {externalDomains.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                          {externalDomains.slice(0, 3).map((domain) => (
                            <Chip key={domain} label={domain} size="small" color="info" />
                          ))}
                          {externalDomains.length > 3 && (
                            <Typography variant="caption" color="text.secondary">+{externalDomains.length - 3} more</Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}

              {loadingMore && (
                <Box sx={{ py: 1 }} role="status">
                  <Skeleton variant="rounded" height={56} sx={{ borderRadius: 'var(--q-radius-lg)' }} />
                  <Typography variant="caption" color="text.secondary">Loading older meetings...</Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        {importing && (
          <Box sx={{ mt: 2 }} role="status">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 2 }}>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
                Importing: {progress.currentName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {progress.current}/{progress.total}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress.total ? (progress.current / progress.total) * 100 : 0}
              aria-label="Import progress"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeImport} disabled={importing}>
          {selectedCount > 0 && !importing ? 'Cancel' : 'Close'}
        </Button>
        {meetings.length > 0 && (
          <Button
            variant="contained"
            disabled={selectedCount === 0 || importing}
            onClick={importSelected}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <CloudDownload />}
          >
            {importing
              ? `Importing ${progress.current}/${progress.total}...`
              : selectedCount === 0
                ? 'Select meetings to import'
                : `Import ${selectedCount} meeting${selectedCount !== 1 ? 's' : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * Confirm importing meetings whose attendee domains belong to other customers.
 * @param {object} props
 * @param {ReturnType<import('./useFathomImport').default>} props.fathom - Hook state.
 * @returns {JSX.Element}
 */
export function DomainConflictDialog({ fathom }) {
  const { conflictOpen, conflicts, pendingMeetings, cancelConflict, confirmConflict } = fathom;
  return (
    <Dialog open={conflictOpen} onClose={cancelConflict} maxWidth="sm" fullWidth disableRestoreFocus>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmber color="warning" fontSize="small" />
        Some attendees belong to other customers
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These attendee domains are already linked to other customers. You can still import the meetings into
          this project; they will not be moved to those customers.
        </Typography>
        {conflicts.map((conflict) => (
          <DomainRow
            key={conflict.domain}
            tone="warning"
            icon={<WarningAmber />}
            domain={conflict.domain}
            detail={<>Linked to <strong>{conflict.matchedCustomer?.name || 'another customer'}</strong></>}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={cancelConflict}>Cancel</Button>
        <Button variant="contained" onClick={confirmConflict}>
          Import {pendingMeetings.length} meeting{pendingMeetings.length !== 1 ? 's' : ''} anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * After an import, offer to link new attendee domains to the project's customer.
 * @param {object} props
 * @param {ReturnType<import('./useFathomImport').default>} props.fathom - Hook state.
 * @returns {JSX.Element}
 */
export function DomainReviewDialog({ fathom }) {
  const {
    domainReviewOpen, domainReviewData, domainReviewSaving, toggleDomainReview,
    closeDomainReview, saveDomainReview, projectCustomer,
  } = fathom;
  const unlinked = domainReviewData.filter((d) => d.status === 'unlinked');
  const other = domainReviewData.filter((d) => d.status === 'other_customer');
  const linked = domainReviewData.filter((d) => d.status === 'linked');
  const selectedUnlinkedCount = unlinked.filter((d) => d.selected).length;
  const customerName = projectCustomer?.name || 'Customer';

  return (
    <Dialog open={domainReviewOpen} onClose={closeDomainReview} maxWidth="sm" fullWidth disableRestoreFocus>
      <DialogTitle>Review attendee domains</DialogTitle>
      <DialogContent>
        {other.length > 0 && (
          <>
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {other.length === 1 ? (
                <>
                  <strong>{other[0].domain}</strong> is linked to <strong>{other[0].matchedCustomer?.name}</strong>.
                  The meeting was imported here; check whether it belongs to that customer instead.
                </>
              ) : (
                <>
                  {other.length} domains are linked to other customers. The meetings were imported here; check
                  whether they belong elsewhere.
                </>
              )}
            </Alert>
            {other.map((item) => (
              <DomainRow
                key={item.domain}
                tone="warning"
                icon={<WarningAmber />}
                domain={item.domain}
                detail={<>Linked to <strong>{item.matchedCustomer?.name}</strong></>}
              />
            ))}
          </>
        )}

        {unlinked.length > 0 && (
          <>
            {other.length > 0 && <Divider sx={{ my: 2 }} />}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {unlinked.length === 1 ? 'This domain is' : `These ${unlinked.length} domains are`} not linked to any
              customer. {projectCustomer && <>Add them to <strong>{customerName}</strong> so future meetings match automatically.</>}
            </Typography>
            {unlinked.map((item) => (
              <DomainRow
                key={item.domain}
                domain={item.domain}
                detail={`Add to ${customerName} for auto-matching`}
                onClick={() => toggleDomainReview(item.domain)}
                leading={(
                  <Checkbox
                    checked={item.selected}
                    onChange={() => toggleDomainReview(item.domain)}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    inputProps={{ 'aria-label': `Add ${item.domain} to ${customerName}` }}
                  />
                )}
              />
            ))}
          </>
        )}

        {linked.length > 0 && (
          <>
            {(other.length > 0 || unlinked.length > 0) && <Divider sx={{ my: 2 }} />}
            {linked.map((item) => (
              <DomainRow
                key={item.domain}
                tone="success"
                icon={<CheckCircle />}
                domain={item.domain}
                detail={`Already linked to ${customerName}`}
                trailing={<Chip label="Linked" size="small" color="success" />}
              />
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDomainReview} disabled={domainReviewSaving}>
          {unlinked.length > 0 ? 'Skip' : 'Got it'}
        </Button>
        {unlinked.length > 0 && (
          <Button
            onClick={saveDomainReview}
            variant="contained"
            disabled={domainReviewSaving || selectedUnlinkedCount === 0}
            startIcon={domainReviewSaving ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
          >
            {domainReviewSaving
              ? 'Saving...'
              : `Add ${selectedUnlinkedCount} domain${selectedUnlinkedCount !== 1 ? 's' : ''} to ${customerName}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
